import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Calendar, ArrowLeft, Upload } from 'lucide-react';
import { getErrorMessage } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All medical specializations
const ALL_SPECIALIZATIONS = [
  'Allergy and Immunology',
  'Anesthesiology',
  'Cardiology',
  'Cardiovascular Surgery',
  'Critical Care Medicine',
  'Dentistry',
  'Dermatology',
  'Diabetology',
  'Emergency Medicine',
  'Endocrinology',
  'ENT (Otolaryngology)',
  'Family Medicine',
  'Gastroenterology',
  'General Medicine',
  'General Surgery',
  'Geriatrics',
  'Gynecology',
  'Hematology',
  'Hepatology',
  'Infectious Disease',
  'Internal Medicine',
  'Interventional Radiology',
  'Neonatology',
  'Nephrology',
  'Neurology',
  'Neurosurgery',
  'Obstetrics',
  'Obstetrics and Gynecology',
  'Oncology',
  'Ophthalmology',
  'Oral and Maxillofacial Surgery',
  'Orthopedics',
  'Pain Management',
  'Pathology',
  'Pediatrics',
  'Pediatric Surgery',
  'Physical Medicine and Rehabilitation',
  'Plastic Surgery',
  'Podiatry',
  'Psychiatry',
  'Pulmonology',
  'Radiology',
  'Rheumatology',
  'Sports Medicine',
  'Thoracic Surgery',
  'Urology',
  'Vascular Surgery',
  'Ayurveda',
  'Homeopathy',
  'Unani',
  'Naturopathy',
  'Physiotherapy'
];

// All major Indian cities
const ALL_CITIES = [
  'Agra',
  'Ahmedabad',
  'Ajmer',
  'Aligarh',
  'Allahabad',
  'Amritsar',
  'Aurangabad',
  'Bangalore',
  'Bareilly',
  'Belgaum',
  'Bhavnagar',
  'Bhilai',
  'Bhopal',
  'Bhubaneswar',
  'Bikaner',
  'Chandigarh',
  'Chennai',
  'Coimbatore',
  'Cuttack',
  'Dehradun',
  'Delhi',
  'Dhanbad',
  'Durgapur',
  'Erode',
  'Faridabad',
  'Firozabad',
  'Ghaziabad',
  'Gorakhpur',
  'Guntur',
  'Gurgaon',
  'Guwahati',
  'Gwalior',
  'Hubli',
  'Hyderabad',
  'Indore',
  'Jabalpur',
  'Jaipur',
  'Jalandhar',
  'Jammu',
  'Jamnagar',
  'Jamshedpur',
  'Jhansi',
  'Jodhpur',
  'Kanpur',
  'Kochi',
  'Kolhapur',
  'Kolkata',
  'Kota',
  'Kozhikode',
  'Lucknow',
  'Ludhiana',
  'Madurai',
  'Mangalore',
  'Mathura',
  'Meerut',
  'Moradabad',
  'Mumbai',
  'Mysore',
  'Nagpur',
  'Nanded',
  'Nashik',
  'Navi Mumbai',
  'Noida',
  'Patna',
  'Pimpri-Chinchwad',
  'Pondicherry',
  'Pune',
  'Raipur',
  'Rajkot',
  'Ranchi',
  'Salem',
  'Sangli',
  'Siliguri',
  'Solapur',
  'Srinagar',
  'Surat',
  'Thane',
  'Thiruvananthapuram',
  'Tiruchirappalli',
  'Tiruppur',
  'Udaipur',
  'Ujjain',
  'Vadodara',
  'Varanasi',
  'Vasai-Virar',
  'Vijayawada',
  'Visakhapatnam',
  'Warangal'
];

export default function AuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // Load saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('mrcall_saved_email');
    if (savedEmail) {
      setLoginData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'doctor',
    specialization: '',
    clinic_name: '',
    clinic_address: '',
    city: '',
    company_name: '',
    division: '',
    doctor_photo: '',
    mr_photo: '',
    visiting_card: '',
    mr_visiting_card: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    const trimmedEmail = (loginData.email || '').trim();
    const payload = {
      ...loginData,
      email: trimmedEmail,
    };
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, payload);
      
      // Save or remove email based on Remember Me checkbox (never store plain text passwords)
      if (rememberMe) {
        localStorage.setItem('mrcall_saved_email', trimmedEmail);
      } else {
        localStorage.removeItem('mrcall_saved_email');
      }
      localStorage.removeItem('mrcall_saved_login'); // clean up legacy plain text storage if present
      
      login(response.data.token, response.data.user);
      toast.success('Logged in successfully!');
      
      if (response.data.user.role === 'doctor') navigate('/doctor');
      else if (response.data.user.role === 'mr') navigate('/mr');
      else if (response.data.user.role === 'admin') navigate('/admin');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Trim string inputs
    const trimmedEmail = (signupData.email || '').trim();
    const trimmedPassword = (signupData.password || '').trim();
    const trimmedName = (signupData.full_name || '').trim();
    const trimmedPhone = (signupData.phone || '').trim();

    if (!trimmedEmail) {
      toast.error('Email is required');
      return;
    }
    if (!trimmedPassword || trimmedPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (!trimmedName) {
      toast.error('Full name is required');
      return;
    }

    const payload = {
      ...signupData,
      email: trimmedEmail,
      password: trimmedPassword,
      full_name: trimmedName,
      phone: trimmedPhone,
      specialization: signupData.specialization || null,
      clinic_name: (signupData.clinic_name || '').trim() || null,
      clinic_address: (signupData.clinic_address || '').trim() || null,
      city: signupData.city || null,
      company_name: (signupData.company_name || '').trim() || null,
      division: (signupData.division || '').trim() || null,
      doctor_photo: signupData.doctor_photo || null,
      mr_photo: signupData.mr_photo || null,
      visiting_card: signupData.visiting_card || null,
      mr_visiting_card: signupData.mr_visiting_card || null,
    };

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, payload);
      login(response.data.token, response.data.user);
      toast.success('Account created successfully!');
      
      if (response.data.user.role === 'doctor') navigate('/doctor');
      else if (response.data.user.role === 'mr') navigate('/mr');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVisitingCardUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignupData({ ...signupData, visiting_card: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDoctorPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignupData({ ...signupData, doctor_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMRPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignupData({ ...signupData, mr_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBrochureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignupData({ ...signupData, mr_visiting_card: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-subtle to-white flex items-center justify-center px-6 py-12">
      <div className="absolute top-6 left-6">
        <Button
          data-testid="back-to-home-btn"
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-slate-600 hover:text-primary"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-12 w-12 bg-primary rounded-full flex items-center justify-center">
              <Calendar className="h-7 w-7 text-white" />
            </div>
            <span className="text-3xl font-heading font-bold text-primary tracking-tight">MRcall</span>
          </div>
          <p className="text-slate-600">Welcome to the healthcare appointment platform</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-lg">
          <Tabs value={isLogin ? 'login' : 'signup'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    required
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    required
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember-me"
                    data-testid="remember-me-checkbox"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked)}
                  />
                  <Label 
                    htmlFor="remember-me" 
                    className="text-sm font-normal text-slate-600 cursor-pointer"
                  >
                    Remember my login details
                  </Label>
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  className="w-full rounded-full py-6 bg-primary hover:bg-primary-hover text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <Label htmlFor="signup-role">I am a</Label>
                  <Select
                    value={signupData.role}
                    onValueChange={(value) => setSignupData({ ...signupData, role: value })}
                  >
                    <SelectTrigger data-testid="signup-role-select" className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-50">
                      <SelectItem value="doctor" data-testid="role-doctor">Doctor</SelectItem>
                      <SelectItem value="mr" data-testid="role-mr">Medical Representative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="signup-name">Full Name</Label>
                  <Input
                    id="signup-name"
                    data-testid="signup-name-input"
                    required
                    value={signupData.full_name}
                    onChange={(e) => setSignupData({ ...signupData, full_name: e.target.value })}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    data-testid="signup-email-input"
                    type="email"
                    required
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="signup-phone">Phone Number</Label>
                  <Input
                    id="signup-phone"
                    data-testid="signup-phone-input"
                    type="tel"
                    placeholder="+1234567890"
                    required
                    value={signupData.phone}
                    onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    data-testid="signup-password-input"
                    type="password"
                    required
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Must be at least 6 characters.
                  </p>
                </div>

                {signupData.role === 'doctor' && (
                  <>
                    <div>
                      <Label htmlFor="signup-specialization">Specialization</Label>
                      <Select
                        value={signupData.specialization}
                        onValueChange={(value) => setSignupData({ ...signupData, specialization: value })}
                      >
                        <SelectTrigger data-testid="signup-specialization-input" className="mt-1.5">
                          <SelectValue placeholder="Select your specialization" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {ALL_SPECIALIZATIONS.map(spec => (
                            <SelectItem key={spec} value={spec}>
                              {spec}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="signup-clinic-name">Clinic Name</Label>
                      <Input
                        id="signup-clinic-name"
                        data-testid="signup-clinic-name-input"
                        required
                        placeholder="e.g., Apollo Hospital"
                        value={signupData.clinic_name}
                        onChange={(e) => setSignupData({ ...signupData, clinic_name: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="signup-clinic">Clinic Address</Label>
                      <Input
                        id="signup-clinic"
                        data-testid="signup-clinic-input"
                        required
                        placeholder="Complete clinic address"
                        value={signupData.clinic_address}
                        onChange={(e) => setSignupData({ ...signupData, clinic_address: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="doctor-photo">Your Photo (Optional)</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleDoctorPhotoUpload}
                        className="hidden"
                        id="doctor-photo"
                      />
                      <label
                        htmlFor="doctor-photo"
                        className="mt-1.5 flex items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary transition-colors"
                      >
                        {signupData.doctor_photo ? (
                          <img src={signupData.doctor_photo} alt="Doctor photo" className="h-full object-cover rounded-lg" />
                        ) : (
                          <div className="text-center">
                            <Upload className="mx-auto h-6 w-6 text-slate-400" />
                            <p className="mt-1 text-xs text-slate-600">Upload your photo</p>
                            <p className="text-xs text-slate-400">Helps MRs recognize you</p>
                          </div>
                        )}
                      </label>
                    </div>
                    <div>
                      <Label htmlFor="visiting-card">Visiting Card (Optional)</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleVisitingCardUpload}
                        className="hidden"
                        id="visiting-card"
                      />
                      <label
                        htmlFor="visiting-card"
                        className="mt-1.5 flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary transition-colors"
                      >
                        {signupData.visiting_card ? (
                          <img src={signupData.visiting_card} alt="Visiting card" className="h-full object-contain" />
                        ) : (
                          <div className="text-center">
                            <Upload className="mx-auto h-6 w-6 text-slate-400" />
                            <p className="mt-1 text-xs text-slate-600">Upload visiting card</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </>
                )}

                {signupData.role === 'mr' && (
                  <>
                    <div>
                      <Label htmlFor="signup-company">Company Name</Label>
                      <Input
                        id="signup-company"
                        data-testid="signup-company-input"
                        required
                        placeholder="e.g., Cipla, Sun Pharma"
                        value={signupData.company_name}
                        onChange={(e) => setSignupData({ ...signupData, company_name: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="signup-division">Division</Label>
                      <Input
                        id="signup-division"
                        data-testid="signup-division-input"
                        required
                        placeholder="e.g., Cardiology, Oncology"
                        value={signupData.division}
                        onChange={(e) => setSignupData({ ...signupData, division: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mr-photo">Your Photo (Optional)</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleMRPhotoUpload}
                        className="hidden"
                        id="mr-photo"
                      />
                      <label
                        htmlFor="mr-photo"
                        className="mt-1.5 flex items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary transition-colors"
                      >
                        {signupData.mr_photo ? (
                          <img src={signupData.mr_photo} alt="MR photo" className="h-full object-cover rounded-lg" />
                        ) : (
                          <div className="text-center">
                            <Upload className="mx-auto h-6 w-6 text-slate-400" />
                            <p className="mt-1 text-xs text-slate-600">Upload your photo</p>
                            <p className="text-xs text-slate-400">Helps doctors recognize you</p>
                          </div>
                        )}
                      </label>
                    </div>
                    <div>
                      <Label htmlFor="mr-visiting-card">Visiting Card (Optional)</Label>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleBrochureUpload}
                        className="hidden"
                        id="mr-visiting-card"
                      />
                      <label
                        htmlFor="mr-visiting-card"
                        className="mt-1.5 flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary transition-colors"
                      >
                        {signupData.mr_visiting_card ? (
                          <div className="text-center">
                            <Upload className="mx-auto h-6 w-6 text-primary" />
                            <p className="mt-1 text-xs text-primary font-medium">Visiting card uploaded</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <Upload className="mx-auto h-6 w-6 text-slate-400" />
                            <p className="mt-1 text-xs text-slate-600">Upload visiting card</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="signup-city">City</Label>
                  <Select
                    value={signupData.city}
                    onValueChange={(value) => setSignupData({ ...signupData, city: value })}
                  >
                    <SelectTrigger data-testid="signup-city-input" className="mt-1.5">
                      <SelectValue placeholder="Select your city" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {ALL_CITIES.map(city => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  data-testid="signup-submit-btn"
                  type="submit"
                  className="w-full rounded-full py-6 bg-primary hover:bg-primary-hover text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
                
                <p className="text-xs text-center text-slate-500 mt-2">
                  By signing up, you agree to our{' '}
                  <a href="/policy" target="_blank" rel="noreferrer" className="text-primary underline">
                    Terms of Service & Privacy Policy
                  </a>
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}